import React, { useState, useEffect, useCallback, useContext } from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import UserContext from '../context/UserContext';

// Countdown component for upcoming streams
const CountdownTimer = ({ publishedAt }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const publishDate = new Date(publishedAt);
      const diff = publishDate - now;

      if (diff <= 0) {
        setTimeLeft('Starting soon...');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [publishedAt]);

  return <span className="text-sm text-gray-600">{timeLeft}</span>;
};

const Livestreams = () => {
  const { user, loading: authLoading } = useContext(UserContext);
  const [livestreams, setLivestreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLivestreams = useCallback(async () => {
    if (!user) {
      setError('Please log in to view livestreams.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch livestreams from Firestore
      const livestreamsRef = collection(db, 'Livestreams');
      const q = query(livestreamsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);

      const streams = [];
      querySnapshot.forEach((doc) => {
        streams.push({ id: doc.id, ...doc.data() });
      });

      setLivestreams(streams);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching livestreams:', err);
      setError('Failed to fetch livestreams. Please try again later.');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchLivestreams();
    } else if (!authLoading && !user) {
      setError('Please log in to view livestreams.');
      setLoading(false);
    }
  }, [fetchLivestreams, user, authLoading]);

  const getEmbedUrl = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
  };

  // Don't render anything if auth is still loading or user is not authenticated
  if (authLoading || !user) {
    return (
      <div className="min-h-screen p-8">
        <h1 className="text-3xl font-bold mb-8 text-teens-gray">Livestreams</h1>
        <div className="text-center text-teens-gray">
          {authLoading ? 'Loading...' : 'Please log in to view livestreams.'}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen p-8">
        <h1 className="text-3xl font-bold mb-8 text-teens-gray">Livestreams</h1>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kids-blue"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8">
        <h1 className="text-3xl font-bold mb-8 text-teens-gray">Livestreams</h1>
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  // Separate live and upcoming streams
  const liveStreams = livestreams.filter(stream => stream.liveBroadcastContent === 'live');
  const upcomingStreams = livestreams.filter(stream => stream.liveBroadcastContent === 'upcoming');

  return (
    <div className="min-h-screen p-8">
      <h1 className="text-3xl font-bold mb-8 text-teens-gray">Livestreams</h1>

      {livestreams.length === 0 ? (
        <div className="text-center text-teens-gray">
          No active or upcoming livestreams found.
        </div>
      ) : (
        <div className="space-y-12">
          {/* Live Streams Section */}
          {liveStreams.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-teens-gray">🔴 Live Now</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {liveStreams.map((stream) => (
                  <div key={stream.id} className="bg-silver rounded-custom overflow-hidden shadow-lg">
                    <div className="aspect-video">
                      <iframe
                        src={getEmbedUrl(stream.id)}
                        title={stream.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full rounded-t-custom"
                      ></iframe>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2 text-black">{stream.title}</h3>
                      <p className="text-black text-sm mb-2 opacity-75">{stream.channelTitle}</p>
                      <div className="flex items-center">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-red-600 text-white">
                          LIVE
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Streams Section */}
          {upcomingStreams.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-teens-gray">⏰ Scheduled Livestreams</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingStreams.map((stream) => (
                  <div key={stream.id} className="bg-silver rounded-custom overflow-hidden shadow-lg">
                    <div className="aspect-video">
                      <iframe
                        src={getEmbedUrl(stream.id)}
                        title={stream.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full rounded-t-custom"
                      ></iframe>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2 text-black">{stream.title}</h3>
                      <p className="text-black text-sm mb-2 opacity-75">{stream.channelTitle}</p>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-600 text-white">
                          UPCOMING
                        </span>
                        <CountdownTimer publishedAt={stream.publishedAt} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Livestreams;
